import type { FlagName } from '@platform/feature-flags';
import { notFound, redirect } from '@tanstack/react-router';
import { returnToFromSearch } from './relativePath';
import type { RouterContext } from './router';
import { expandSearchContext } from './searchContext';

/** What the composed `beforeLoad` reads. A subset of TanStack's argument, so the real one satisfies it. */
export interface PageBeforeLoadArgs {
  context: RouterContext;
  location: { pathname: string; searchStr: string };
}

export interface PageBeforeLoadOptions {
  /** Feature flag gate; when set, `beforeLoad` throws `notFound()` unless the flag resolves to `true`. */
  flag?: FlagName;
  /** Redirect here instead of throwing `notFound()` when `flag` is off. Ignored without `flag`. */
  flagFallbackHref?: string;
  /**
   * Entry side effect, e.g. seeding a journey from the URL. Between the `flag` gate and
   * `redirectAuthenticatedTo`, so it survives that redirect where a `loader` would not run at all.
   * Pair it with `preload: false`.
   */
  onEntry?: (args: PageBeforeLoadArgs) => void;
  /** Anonymous-only gate (e.g. register): authenticated visitors are redirected to `?to=` if the URL carries one, else here. Runs after the `flag` check, so a flag-off route still 404s instead of redirecting. */
  redirectAuthenticatedTo?: string;
  /**
   * Extra `?context=` envelope keys this route may promote to search params, on top of the global
   * ones (`to`). Declare only what the route acts on: an envelope arrives on a URL, so the allowlist
   * is the boundary between "a link we minted" and "a param someone injected".
   */
  searchContextKeys?: readonly string[];
}

/**
 * Composes a page route's entry policy: flag gate, `?context=` expansion, entry side effect,
 * anonymous-only gate, in that order.
 *
 * Always returns a function — every page route can receive an envelope, so expansion is not
 * something a route opts into. The flag gate stays first (a route that is off should 404 rather than
 * tidy its URL first), and expansion precedes `onEntry`/`redirectAuthenticatedTo` because it
 * redirects: those two then run against the expanded URL on the next pass, so a promoted `to` is
 * visible to the journey capture and to the anonymous-only gate alike.
 */
export function composePageBeforeLoad(options: PageBeforeLoadOptions): (args: PageBeforeLoadArgs) => void {
  const { flag, flagFallbackHref, onEntry, redirectAuthenticatedTo, searchContextKeys } = options;

  return function pageBeforeLoad(args: PageBeforeLoadArgs) {
    if (flag && args.context.featureFlags?.getFlag(flag) !== true) {
      if (flagFallbackHref) {
        throw redirect({ href: flagFallbackHref, reloadDocument: true });
      }
      throw notFound();
    }
    redirectExpandedSearchContext(args, searchContextKeys);
    onEntry?.(args);
    if (redirectAuthenticatedTo && args.context.auth.status === 'authenticated') {
      // `href`, not `to`: the target is any in-app path, not a typed route.
      const returnTo = returnToFromSearch(args.location.searchStr);
      throw returnTo ? redirect({ href: returnTo, replace: true }) : redirect({ to: redirectAuthenticatedTo, replace: true });
    }
  };
}

/**
 * Redirects to the same page with `?context=` unpacked into plain search params, or returns when
 * there is no envelope to unpack.
 *
 * `href`, not `to`: the target is this location with a rewritten query, not a typed route.
 * `location.pathname` is the router's internal form (de-localized, basepath stripped), which
 * `buildLocation` localizes back on the way out — the same round-trip any in-app navigation makes.
 * `replace` keeps the envelope URL out of history, so back doesn't land on it and expand again.
 */
function redirectExpandedSearchContext(args: PageBeforeLoadArgs, searchContextKeys: readonly string[] | undefined): void {
  const expanded = expandSearchContext(args.location.searchStr, searchContextKeys);
  if (expanded === null) {
    return;
  }
  const { pathname } = args.location;
  throw redirect({ href: expanded === '' ? pathname : `${pathname}?${expanded}`, replace: true });
}
